<template>
  <div class="admin-content-container">
    <div class="space-y-8">
      <div class="flex justify-between items-center">
        <h2 class="font-header text-[3rem] font-bold text-primary">Delivery Options</h2>
        <button v-if="!showForm" @click="showForm = true; editingOption = null"
          class="px-6 py-3 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-body">
          Add New Option
        </button>
      </div>

      <div v-if="showForm" class="bg-white rounded-lg shadow-sm p-8 md:p-12">
        <div class="flex justify-between items-center mb-8">
          <h3 class="font-header text-[1.5rem] font-bold text-primary">
            {{ editingOption ? 'Edit Delivery Option' : 'Add New Delivery Option' }}
          </h3>
          <button @click="cancelForm" class="font-body text-body text-gray-600 hover:text-gray-800 font-bold">
            Cancel
          </button>
        </div>

        <form @submit.prevent="saveOption" class="space-y-6">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label class="block font-body text-body font-bold text-primary mb-2">Name *</label>
              <input v-model="formData.name" type="text" required
                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body"
                placeholder="e.g., Standard Shipping" />
            </div>


            <div>
              <label class="block font-body text-body font-bold text-primary mb-2">Active Status</label>
              <label class="flex items-center gap-3 cursor-pointer">
                <input v-model="formData.is_active" type="checkbox"
                  class="w-4 h-4 bg-white border-gray-300 text-gray-900 focus:ring-gray-400 focus:ring-2 rounded" />
                <span class="font-body text-body">Active</span>
              </label>
            </div>

            <div class="lg:col-span-2">
              <label class="block font-body text-body font-bold text-primary mb-2">Description</label>
              <textarea v-model="formData.description" rows="3"
                class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body"
                placeholder="Optional description of this delivery option"></textarea>
            </div>

            <div class="lg:col-span-2">
              <label class="block font-body text-body font-bold text-primary mb-2">Information Fields</label>
              <p class="font-body text-body text-gray-600 mb-3">Add custom information fields (e.g., "Cutoff Time: 12:00
                PM", "Processing: 1-2 days")</p>
              <div class="space-y-3">
                <div v-for="(field, index) in formData.info_fields" :key="index" class="flex gap-2">
                  <input v-model="field.label" type="text"
                    class="flex-1 px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                    placeholder="Label (e.g., Cutoff Time)" />
                  <input v-model="field.value" type="text"
                    class="flex-1 px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                    placeholder="Value (e.g., 12:00 PM)" />
                  <button type="button" @click="removeInfoField(index)"
                    class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-marketing">
                    Remove
                  </button>
                </div>
                <button type="button" @click="addInfoField"
                  class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing">
                  Add Information Field
                </button>
              </div>
            </div>

            <div class="lg:col-span-2">
              <label class="block text-sm font-marketing font-semibold text-primary mb-2">
                Available Regions *
              </label>
              <p class="text-xs text-gray-600 mb-3 font-sans">Specify countries (e.g., US, CA, UK) or "Worldwide"</p>
              <div class="space-y-2">
                <div v-for="(region, index) in formData.available_regions" :key="index" class="flex gap-2">
                  <input v-model="formData.available_regions[index]" type="text"
                    class="flex-1 px-4 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans"
                    placeholder="e.g., US, CA, UK, or Worldwide" />
                  <button type="button" @click="removeRegion(index)"
                    class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-marketing">
                    Remove
                  </button>
                </div>
                <button type="button" @click="addRegion"
                  class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-marketing">
                  Add Region
                </button>
              </div>
            </div>
          </div>

          <div class="flex gap-4">
            <button type="submit"
              class="px-6 py-3 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing font-semibold">
              {{ editingOption ? 'Update' : 'Create' }}
            </button>
            <button type="button" @click="cancelForm"
              class="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-marketing font-semibold">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div v-if="loading" class="bg-white rounded-lg shadow-sm p-8 text-center">
        <Loader size="medium" />
      </div>

      <div v-else-if="deliveryOptions.length === 0"
        class="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500 font-marketing">
        No delivery options found. Click "Add New Option" to create one.
      </div>

      <div v-else class="bg-white rounded-lg shadow-sm overflow-hidden">
        <div class="divide-y divide-creme-200">
          <div v-for="option in deliveryOptions" :key="option.id" class="p-6 hover:bg-creme-50 transition-colors">
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1">
                <div class="flex items-center gap-3 mb-2">
                  <h4 class="text-xl font-marketing font-semibold text-primary">{{ option.name }}</h4>
                  <span
                    :class="['px-2 py-1 rounded text-xs font-marketing', option.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800']">
                    {{ option.is_active ? 'Active' : 'Inactive' }}
                  </span>
                  <span v-if="option.is_default"
                    class="px-2 py-1 rounded text-xs font-marketing bg-blue-100 text-blue-800">
                    Default
                  </span>
                </div>
                <p v-if="option.description" class="text-sm text-gray-600 font-sans mb-2">{{ option.description }}</p>
                <div class="space-y-2">
                  <div v-if="option.info_fields && option.info_fields.length > 0"
                    class="flex flex-wrap gap-4 text-sm text-gray-600 font-sans">
                    <span v-for="(field, idx) in option.info_fields" :key="idx">
                      <strong>{{ field.label }}:</strong> {{ field.value }}
                    </span>
                  </div>
                  <div v-if="option.available_regions && option.available_regions.length > 0"
                    class="flex flex-wrap gap-4 text-sm text-gray-600 font-sans">
                    <span><strong>Regions:</strong> {{ option.available_regions.join(', ') }}</span>
                  </div>
                </div>
              </div>
              <div class="flex gap-2 flex-shrink-0">
                <button @click="editOption(option)"
                  class="px-4 py-2 text-sm text-green-700 hover:text-green-800 font-marketing">Edit</button>
                <button v-if="!option.is_default" @click="deleteOption(option.id)"
                  class="px-4 py-2 text-sm text-red-600 hover:text-red-700 font-marketing">Delete</button>
                <span v-else class="px-4 py-2 text-sm text-gray-400 font-marketing cursor-not-allowed">Cannot
                  Delete</span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="pagination && pagination.total_pages > 1"
          class="p-4 flex justify-between items-center border-t border-creme-200">
          <button @click="loadDeliveryOptions(currentPage - 1)" :disabled="!pagination.prev_page"
            :class="['px-4 py-2 rounded-md font-marketing', pagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
            Previous
          </button>
          <span class="font-marketing text-gray-700">
            Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
          </span>
          <button @click="loadDeliveryOptions(currentPage + 1)" :disabled="!pagination.next_page"
            :class="['px-4 py-2 rounded-md font-marketing', pagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { adminService } from '../../services/admin'
import Loader from '../Loader.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import { useScrollToTop } from '../../composables/useScrollToTop'

const deliveryOptions = ref([])
const showForm = ref(false)
const editingOption = ref(null)
const { loading, setLoading } = useLoadingState(false)
const { scrollToTop } = useScrollToTop()
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)

const formData = ref({
  name: '',
  description: '',
  info_fields: [],
  available_regions: [],
  is_active: true
})

const loadDeliveryOptions = async (page = 1) => {
  setLoading(true)
  try {
    const response = await adminService.getDeliveryOptions(page, limit.value)
    if (response.data && response.pagination) {
      deliveryOptions.value = response.data || []
      pagination.value = response.pagination
      currentPage.value = page
    } else {
      deliveryOptions.value = []
      pagination.value = null
    }
  } catch (error) {
    console.error('Failed to load delivery options:', error)
    deliveryOptions.value = []
    pagination.value = null
  } finally {
    setLoading(false)
  }
}

const addInfoField = () => {
  formData.value.info_fields.push({ label: '', value: '' })
}

const removeInfoField = (index) => {
  formData.value.info_fields.splice(index, 1)
}

const addRegion = () => {
  formData.value.available_regions.push('')
}

const removeRegion = (index) => {
  formData.value.available_regions.splice(index, 1)
}

const resetForm = () => {
  formData.value = {
    name: '',
    description: '',
    info_fields: [],
    available_regions: [],
    is_active: true
  }
  editingOption.value = null
}

const cancelForm = () => {
  resetForm()
  showForm.value = false
  scrollToTop()
}

const editOption = (option) => {
  editingOption.value = option
  formData.value = {
    name: option.name,
    description: option.description || '',
    info_fields: option.info_fields && Array.isArray(option.info_fields)
      ? option.info_fields.map(f => ({ label: f.label || '', value: f.value || '' }))
      : [],
    available_regions: option.available_regions && Array.isArray(option.available_regions)
      ? [...option.available_regions]
      : [],
    is_active: option.is_active
  }
  showForm.value = true
}

const saveOption = async () => {
  const regions = formData.value.available_regions.filter(r => r.trim()).map(r => r.trim())
  if (regions.length === 0) {
    alert('Please add at least one region (e.g., US, CA, UK, or Worldwide)')
    return
  }

  try {
    const payload = {
      name: formData.value.name.trim(),
      description: formData.value.description.trim() || null,
      info_fields: formData.value.info_fields
        .filter(f => f.label.trim() && f.value.trim())
        .map(f => ({ label: f.label.trim(), value: f.value.trim() })),
      available_regions: regions,
      is_active: formData.value.is_active
    }

    if (editingOption.value) {
      await adminService.updateDeliveryOption(editingOption.value.id, payload)
    } else {
      await adminService.createDeliveryOption(payload)
    }

    await loadDeliveryOptions(currentPage.value)
    cancelForm()
  } catch (error) {
    console.error('Failed to save delivery option:', error)
    alert('Failed to save delivery option. Please try again.')
  }
}

const deleteOption = async (id) => {
  if (!confirm('Are you sure you want to delete this delivery option?')) {
    return
  }

  try {
    await adminService.deleteDeliveryOption(id)
    await loadDeliveryOptions(currentPage.value)
  } catch (error) {
    console.error('Failed to delete delivery option:', error)
    alert('Failed to delete delivery option. Please try again.')
  }
}

onMounted(() => {
  loadDeliveryOptions(1)
})
</script>
